import { useContainerBlockContext } from '@lighthouse/block'
import { FloatBlock } from '@lighthouse/block/src/blocks'
import type { FloatBlockAbstract } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import {
    buildBlockUniqueKey,
    flexMinSizeCSS,
    getBlockAncestors,
    getCursorStyle,
    useApplicationContext,
    useAtomAction,
    useFloatBoxContext
} from '@lighthouse/shared'
import { useStore } from 'jotai'
import React, { useMemo } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'

import { pageBlockContextIndexAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import { AsideType } from '@/atoms/page/types'
import { useCurrentStackIdContext } from '@/contexts/PageContext'

interface FloatBlockProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSubmit'> {
    blockData: FloatBlockAbstract
    node: FlowLayoutNode
}

const SCxContainer = styled.div`
    ${flexMinSizeCSS}
    width: 100%;
    height: 100%;
    display: flex;
`

const FloatBlockController: React.FC<FloatBlockProps> = ({ blockData, node, style, ...rest }) => {
    const { updateDragNode } = useFloatBoxContext(blockData.id)
    const stackId = useCurrentStackIdContext()
    const store = useStore()
    const { run: setPageInternalState } = useAtomAction(pageInternalStateAtomFamily(stackId))
    const { scope } = useContainerBlockContext()

    useMount(() => {
        updateDragNode?.()
        // 若当前选中已是本浮层或本浮层内节点（例如用户在 PageLayers 点了浮层下的子节点），不覆盖，避免「列表选中子节点后被浮层 mount 抢回选中」
        const internalState = store.get(pageInternalStateAtomFamily(stackId))
        const selected = internalState?.selectedNodes?.[0]
        if (selected) {
            const isThisFloat = selected.id === blockData.id && selected.scope === scope
            if (isThisFloat) {
                return
            }
            const index = store.get(pageBlockContextIndexAtom)
            if (index) {
                const selectedKey = buildBlockUniqueKey(selected.id, selected.scope)
                const ancestors = getBlockAncestors(selectedKey, index)
                const isInsideThisFloat = ancestors.some(ctx => ctx.block.id === blockData.id && ctx.scope === scope)
                if (isInsideThisFloat) {
                    return
                }
            }
        }
        setPageInternalState(draft => {
            draft.selectedNodes = [{ id: blockData.id, scope }]
            draft.asideType = AsideType.BLOCK
        })
    })

    const { appId = '' } = useApplicationContext()
    const { cursor } = blockData.config

    const cursorValue = useMemo(() => {
        const isModify = cursor?.enable && cursor?.value === 'auto'
        if (isModify) {
            return 'auto'
        }

        return getCursorStyle(appId, cursor).cursor
    }, [appId, cursor])

    return (
        <SCxContainer>
            <FloatBlock node={node} blockData={blockData} {...rest} style={{ ...style, cursor: cursorValue }} />
        </SCxContainer>
    )
}

export default FloatBlockController
